import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EventType, type EventMessage } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'

import { decodeReturnTo } from '../auth/use-return-to'

/**
 * /sign-in/callback handler.
 *
 * MSAL processes the redirect response in `AuthProvider` on first mount;
 * by the time this component renders the user typically already has an
 * active account. This page reads the OIDC `state` parameter to restore
 * the original `returnTo` path (sanitised by `decodeReturnTo`), then
 * navigates there. If `state` is empty / invalid, fall back to /accounts.
 *
 * Why not just use `<Navigate>` on mount: MSAL emits a
 * `HANDLE_REDIRECT_END` / `LOGIN_SUCCESS` event slightly after our render
 * cycle in some cases. We subscribe to MSAL events as a safety net so the
 * navigation does not race with `instance.handleRedirectPromise()`.
 */
export function SignInCallbackPage(): JSX.Element {
  const navigate = useNavigate()
  const { instance, accounts } = useMsal()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const navigateAfter = (state: string | null | undefined) => {
      if (cancelled) return
      const returnTo = decodeReturnTo(state) ?? '/members'
      navigate(returnTo, { replace: true })
    }

    // If MSAL has already processed the redirect by the time we mount,
    // `accounts` is non-empty - navigate immediately.
    if (accounts.length > 0) {
      // No state to read this late; default to /accounts. (The original
      // returnTo, if any, was consumed by AuthProvider's handleRedirectPromise
      // - we cannot recover it here. Acceptable: M1 returnTo is only used
      // when a deep-link triggers the redirect, and most flows land on
      // /accounts anyway.)
      navigate('/members', { replace: true })
      return
    }

    // Otherwise subscribe to MSAL events.
    const callbackId = instance.addEventCallback((message: EventMessage) => {
      if (cancelled) return
      if (
        message.eventType === EventType.LOGIN_SUCCESS ||
        message.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
      ) {
        const payload = message.payload as { state?: string } | undefined
        navigateAfter(payload?.state)
      } else if (
        message.eventType === EventType.LOGIN_FAILURE ||
        message.eventType === EventType.ACQUIRE_TOKEN_FAILURE
      ) {
        setError('Sign-in failed. Try again, or contact IT if it keeps happening.')
      }
    })

    return () => {
      cancelled = true
      if (callbackId) instance.removeEventCallback(callbackId)
    }
  }, [navigate, instance, accounts])

  if (error) {
    return (
      <div
        role="alert"
        className="mx-auto max-w-[480px] p-murmuration-6 text-murmuration-body text-status-danger"
      >
        {error}
      </div>
    )
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex h-full items-center justify-center text-text-muted"
    >
      <span className="sr-only">Completing sign-in</span>
    </div>
  )
}
